import classNames from 'classnames'
import { FAQJsonLd } from 'gatsby-plugin-next-seo'
import * as Sentry from '@sentry/gatsby'
import { useMemo, useState } from 'react'
import { sanitizeTextContent } from 'src/utils/sanitizeContent'
import type {
  SeoContentAndFaqProcessedItem,
  SeoContentAndFaqTheme,
} from 'src/types/seoContentAndFaq2'

import FaqAccordionItem20 from './FaqAccordionItem'

interface FaqSection20Props {
  title: string
  items: SeoContentAndFaqProcessedItem[]
  theme: SeoContentAndFaqTheme
  isExpanded: boolean
}

const THEME_STYLES = {
  light: {
    title: 'text-fullDark',
  },
  dark: {
    title: 'text-white',
  },
} as const

export default function FaqSection20({
  title,
  items,
  theme,
  isExpanded,
}: FaqSection20Props) {
  const styles = THEME_STYLES[theme]

  const [activeAccordionIndex, setActiveAccordionIndex] = useState<
    number | null
  >(null)

  const sanitizedTitle = useMemo(() => {
    return sanitizeTextContent(title)
  }, [title])

  const validItems = useMemo(() => {
    return items.filter((item) => item.question && item.answerText)
  }, [items])

  const faqSchemaQuestions = useMemo(() => {
    try {
      return validItems
        .map((item) => {
          return {
            question: item.question,
            answer: item.answerText,
          }
        })
        .filter((item) => item.question && item.answer)
    } catch (error) {
      Sentry.captureException(error, {
        level: 'warning',
        tags: {
          component: 'SeoContentAndFaq_2.0',
          context: 'faq-schema',
        },
      })

      return []
    }
  }, [validItems])

  if (!validItems.length) {
    return null
  }

  return (
    <>
      {faqSchemaQuestions.length > 0 && (
        <FAQJsonLd questions={faqSchemaQuestions} />
      )}

      <div
        data-testid="seo-editorial-faq-section"
        className={classNames('mt-6 md:mt-8', !isExpanded && 'hidden')}
      >
        {sanitizedTitle && (
          <h3
            className={classNames(
              'text-xl md:text-[42px] text-center font-bold mb-4',
              styles.title
            )}
          >
            {sanitizedTitle}
          </h3>
        )}

        <ul data-testid="seo-editorial-faq-accordion" className="mt-4">
          {validItems.map((item, index) => {
            const isOpen = activeAccordionIndex === index

            return (
              <FaqAccordionItem20
                key={`${item.question}-${index}`}
                index={index}
                question={item.question}
                answer={item.answer}
                theme={theme}
                isOpen={isOpen}
                onToggle={() => setActiveAccordionIndex(isOpen ? null : index)}
              />
            )
          })}
        </ul>
      </div>
    </>
  )
}
